// ============================================================================
// SCHEDULING — permission definitions.
//
// A typed layer on top of the existing PODGO role model (`app_role` +
// `useAuth().hasRole` + `is_super_admin`). This is *presentation* authority
// only: the database RLS policies are the enforcement boundary. Every entry
// carries the copy needed to explain a disabled control to an administrator.
// ============================================================================

import type { AppRole } from "@/types/auth";

export type SchedulingPermissionKey =
  | "scheduling.groups.view"
  | "scheduling.groups.manage"
  | "scheduling.members.manage"
  | "scheduling.booking_types.view"
  | "scheduling.booking_types.manage"
  | "scheduling.approvals.manage"
  | "scheduling.personal_availability.manage"
  | "scheduling.calendar_privacy.manage_own"
  | "scheduling.calendar_disconnect.manage_own"
  | "scheduling.calendar_disconnect.manage_any"
  | "scheduling.emergency_blocks.manage"
  | "scheduling.audit.view";

/** What the permission applies to, which decides how it is checked. */
export type SchedulingScopeType = "organization" | "group" | "booking_type" | "self";

export interface SchedulingPermissionDefinition {
  key: SchedulingPermissionKey;
  displayName: string;
  description: string;
  /** Roles that normally hold this permission (super admin always does). */
  roles: AppRole[];
  scope: SchedulingScopeType;
  tooltip: string;
  /** Shown when the action is disabled for the current user. */
  deniedReason: string;
}

export const SCHEDULING_PERMISSIONS: Record<
  SchedulingPermissionKey,
  SchedulingPermissionDefinition
> = {
  "scheduling.groups.view": {
    key: "scheduling.groups.view",
    displayName: "View scheduling groups",
    description: "See the list of scheduling groups and their configuration.",
    roles: ["admin", "scheduling_admin"],
    scope: "organization",
    tooltip: "Lets you open the Groups page and read group settings.",
    deniedReason: "You do not have permission to view scheduling groups.",
  },
  "scheduling.groups.manage": {
    key: "scheduling.groups.manage",
    displayName: "Manage scheduling groups",
    description: "Create, edit and archive scheduling groups.",
    roles: ["admin", "scheduling_admin"],
    scope: "group",
    tooltip: "Lets you change a group's name, timezone and status.",
    deniedReason:
      "You cannot manage this group because you are not an authorized Scheduling Admin for it.",
  },
  "scheduling.members.manage": {
    key: "scheduling.members.manage",
    displayName: "Manage group members",
    description: "Add or remove administrators from a scheduling group.",
    roles: ["admin", "scheduling_admin"],
    scope: "group",
    tooltip: "Lets you change who belongs to a group and their routing settings.",
    deniedReason:
      "You cannot change membership because you are not an authorized Scheduling Admin for this group.",
  },
  "scheduling.booking_types.view": {
    key: "scheduling.booking_types.view",
    displayName: "View booking types",
    description: "See booking types and their configuration.",
    roles: ["admin", "scheduling_admin"],
    scope: "organization",
    tooltip: "Lets you open the Booking Types page.",
    deniedReason: "You do not have permission to view booking types.",
  },
  "scheduling.booking_types.manage": {
    key: "scheduling.booking_types.manage",
    displayName: "Manage booking types",
    description: "Create and edit booking types for groups you manage.",
    roles: ["admin", "scheduling_admin"],
    scope: "booking_type",
    tooltip: "Lets you change durations, buffers, access and approval settings.",
    deniedReason:
      "You cannot edit this booking type because you do not manage the group that owns it.",
  },
  "scheduling.approvals.manage": {
    key: "scheduling.approvals.manage",
    displayName: "Manage approvals",
    description: "Configure who approves requests for a booking type.",
    roles: ["admin", "scheduling_admin"],
    scope: "booking_type",
    tooltip: "Lets you choose approvers and how approval decisions are counted.",
    deniedReason:
      "You cannot approve this request because you are not an approver for this booking type.",
  },
  "scheduling.personal_availability.manage": {
    key: "scheduling.personal_availability.manage",
    displayName: "Manage my availability",
    description: "Edit your own working hours, date overrides and time off.",
    roles: ["admin", "scheduling_admin", "studio_admin", "network_admin", "billing_admin"],
    scope: "self",
    tooltip: "Everyone with an admin account manages their own availability.",
    deniedReason:
      "You cannot edit this availability because it belongs to another administrator.",
  },
  "scheduling.calendar_privacy.manage_own": {
    key: "scheduling.calendar_privacy.manage_own",
    displayName: "Manage my calendar privacy",
    description: "Choose how much of your calendar detail is visible to others.",
    roles: ["admin", "scheduling_admin", "studio_admin", "network_admin", "billing_admin"],
    scope: "self",
    tooltip: "Only you control the privacy level of your own connected calendars.",
    deniedReason: "Only the calendar owner can change these privacy settings.",
  },
  "scheduling.calendar_disconnect.manage_own": {
    key: "scheduling.calendar_disconnect.manage_own",
    displayName: "Disconnect my calendars",
    description: "Remove a calendar connection you created.",
    roles: ["admin", "scheduling_admin", "studio_admin", "network_admin", "billing_admin"],
    scope: "self",
    tooltip: "You can always disconnect a calendar you connected yourself.",
    deniedReason: "Only the calendar owner or a Super Admin can disconnect this calendar.",
  },
  "scheduling.calendar_disconnect.manage_any": {
    key: "scheduling.calendar_disconnect.manage_any",
    displayName: "Disconnect any calendar",
    description: "Force-disconnect another administrator's calendar.",
    roles: ["admin"],
    scope: "organization",
    tooltip: "Reserved for Super Admins handling offboarding or security incidents.",
    deniedReason: "Only the calendar owner or a Super Admin can disconnect this calendar.",
  },
  "scheduling.emergency_blocks.manage": {
    key: "scheduling.emergency_blocks.manage",
    displayName: "Manage emergency blocks",
    description: "Block availability across the platform in an emergency.",
    roles: ["admin"],
    scope: "organization",
    tooltip: "Super Admin only — blocks bookings for everyone during an outage or closure.",
    deniedReason: "Only a Super Admin can create organization-wide emergency blocks.",
  },
  "scheduling.audit.view": {
    key: "scheduling.audit.view",
    displayName: "View scheduling audit log",
    description: "Read the record of scheduling configuration changes.",
    roles: ["admin"],
    scope: "organization",
    tooltip: "Audit records are read-only and cannot be edited by anyone.",
    deniedReason: "You do not have permission to view scheduling audit records.",
  },
};

export interface SchedulingPermissionContext {
  roles: AppRole[];
  isSuperAdmin?: boolean;
  /** True when the current user manages the group/booking type in scope. */
  managesScope?: boolean;
}

export interface PermissionResult {
  allowed: boolean;
  /** Populated only when `allowed` is false. */
  reason?: string;
  definition: SchedulingPermissionDefinition;
}

/**
 * Pure evaluator — no React, so it is trivially unit-testable and reusable in
 * services. Scope-bound permissions additionally require `managesScope`,
 * except for platform admins, who manage everything.
 */
export function evaluateSchedulingPermission(
  key: SchedulingPermissionKey,
  ctx: SchedulingPermissionContext,
): PermissionResult {
  const definition = SCHEDULING_PERMISSIONS[key];
  const isPlatformAdmin = ctx.roles.includes("admin") || ctx.isSuperAdmin === true;

  if (isPlatformAdmin) return { allowed: true, definition };

  const hasRole = definition.roles.some((r) => ctx.roles.includes(r));
  if (!hasRole) return { allowed: false, reason: definition.deniedReason, definition };

  const scopeBound = definition.scope === "group" || definition.scope === "booking_type";
  if (scopeBound && ctx.managesScope !== true) {
    return { allowed: false, reason: definition.deniedReason, definition };
  }

  return { allowed: true, definition };
}

/** True when the user should see the Scheduling area at all. */
export function hasAnySchedulingAccess(roles: AppRole[]): boolean {
  return (
    roles.includes("admin") ||
    roles.includes("scheduling_admin") ||
    roles.includes("studio_admin") ||
    roles.includes("network_admin") ||
    roles.includes("billing_admin")
  );
}
